import { StyleSheet, Document, Page, Text, View } from '@react-pdf/renderer'
import React from 'react'
import HeaderOfPDFFashion from './Header'
import FooterOfPDFFashion from './Footer'
import { FashionProductionOrderData } from '../../../pages/ClientPanel/ProductionFashion/FashionPcp'
import { SectionTitle } from '../SectionTitle'
import MainFieldsOfPDF from '../MainFields'

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    paddingTop: 100,
    paddingBottom: 100,
    paddingHorizontal: 40,
  },
  section: {
    marginBottom: 30,
    paddingBottom: 15,
    borderBottom: '1px solid #000',
  },
  itemRow: {
    marginBottom: 12,
    paddingHorizontal: 15,
    paddingVertical: 8,
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    border: '1px solid #000',
  },
  label: {
    fontWeight: 'bold',
    marginBottom: 3,
    fontSize: 9,
    color: '#000',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  value: {
    marginBottom: 4,
    fontSize: 10,
    color: '#000',
    lineHeight: 1.3,
  },
  emptyMessage: {
    fontSize: 10,
    color: '#666',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 20,
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    border: '1px solid #000',
  },
  qrCodeContainer: {
    position: 'absolute',
    bottom: 120,
    right: 40,
    width: 80,
    height: 100,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
})

interface BodyOfPDFFashionType {
  data?: FashionProductionOrderData | null
  qrCodeDataUrl?: string
  cutterName?: string
  dressmakerName?: string
}

const BodyOfPDFFashion: React.FC<BodyOfPDFFashionType> = ({ data, qrCodeDataUrl, cutterName, dressmakerName }) => {

  const getStatusLabel = (status: string): string => {
    const statusMap: { [key: string]: string } = {
      'pending': 'Pendente',
      'iniciado': 'Iniciado',
      'in_progress': 'Em Progresso',
      'completed': 'Concluído',
      'cancelled': 'Cancelado'
    }
    return statusMap[status] || status
  }

  const pushFields = (): { label: string, subLabel?: string, value: string }[] => {
    const fields: { label: string, subLabel?: string, value: string }[] = []

    if (data?.status) {
      fields.push({
        label: 'Status',
        value: getStatusLabel(data.status)
      })
    }

    if (data?.fashionProduct?.name) {
      fields.push({
        label: 'Produto',
        value: data.fashionProduct.name
      })
    }

    if (data?.color?.name) {
      fields.push({
        label: 'Cor',
        value: data.color.name
      })
    }

    if (data?.totalItems) {
      fields.push({
        label: 'Quantidade de Itens',
        value: data.totalItems.toString()
      })
    }

    if (cutterName) {
      fields.push({
        label: 'Cortador',
        value: cutterName
      })
    }

    if (dressmakerName) {
      fields.push({
        label: 'Costureira',
        value: dressmakerName
      })
    }

    return fields
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <HeaderOfPDFFashion />
        <SectionTitle title={`OP-${data?.id || ''}`} />
        <MainFieldsOfPDF infoFields={pushFields()} />

        <View style={styles.section}>
          <Text style={styles.label}>Itens da Ordem de Produção</Text>
          {data?.fashionProductionItems && data.fashionProductionItems.length > 0 ? (
            data.fashionProductionItems.map((item: any, index: number) => {
              const formatDate = (dateString: string | null | undefined) => {
                if (!dateString) return 'N/A'
                try {
                  const date = new Date(dateString)
                  return date.toLocaleDateString('pt-BR')
                } catch {
                  return 'N/A'
                }
              }

              const productData = data?.fashionProduct || item.fashionOrder?.products?.[0]?.fashionProduct
              const collectionName = productData?.group?.family?.collection?.name
              const familyName = productData?.group?.family?.name
              const groupName = productData?.group?.name

              return (
                <View key={index} style={styles.itemRow}>
                  <Text style={styles.value}>
                    <Text style={styles.label}>Pedido:</Text> PED-{item.fashionOrderId}
                  </Text>
                  <Text style={styles.value}>
                    <Text style={styles.label}>Cliente:</Text> {item.fashionOrder?.company?.companyName || 'N/A'}
                  </Text>
                  <Text style={styles.value}>
                    <Text style={styles.label}>Quantidade:</Text> {item.quantityForThisProduction}
                  </Text>
                  {item.size && (
                    <Text style={styles.value}>
                      <Text style={styles.label}>Tamanho:</Text> {item.size}
                    </Text>
                  )}
                  {(productData?.graduation || item.graduation) && (
                    <Text style={styles.value}>
                      <Text style={styles.label}>Graduação:</Text> {productData?.graduation || item.graduation || 'N/A'}
                    </Text>
                  )}
                  {collectionName && (
                    <Text style={styles.value}>
                      <Text style={styles.label}>Coleção:</Text> {collectionName}
                    </Text>
                  )}
                  {familyName && (
                    <Text style={styles.value}>
                      <Text style={styles.label}>Família:</Text> {familyName}
                    </Text>
                  )}
                  {groupName && (
                    <Text style={styles.value}>
                      <Text style={styles.label}>Grupo:</Text> {groupName}
                    </Text>
                  )}
                  {item.fashionOrder?.supplier?.companyName && (
                    <Text style={styles.value}>
                      <Text style={styles.label}>Fornecedor:</Text> {item.fashionOrder.supplier.companyName}
                    </Text>
                  )}
                  {item.fashionOrder?.seller?.name && (
                    <Text style={styles.value}>
                      <Text style={styles.label}>Vendedor:</Text> {item.fashionOrder.seller.name}
                    </Text>
                  )}
                  {item.fashionOrder?.createdAt && (
                    <Text style={styles.value}>
                      <Text style={styles.label}>Data do Pedido:</Text> {formatDate(item.fashionOrder.createdAt)}
                    </Text>
                  )}
                  {item.fashionOrder?.finalDateOfDelivery && (
                    <Text style={styles.value}>
                      <Text style={styles.label}>Data de Entrega:</Text> {formatDate(item.fashionOrder.finalDateOfDelivery)}
                    </Text>
                  )}
                </View>
              )
            })
          ) : (
            <Text style={styles.emptyMessage}>Nenhum item associado a esta ordem de produção.</Text>
          )}
        </View>

        <FooterOfPDFFashion qrCodeDataUrl={qrCodeDataUrl} qrCodeValue={data?.id?.toString()} />
      </Page>
    </Document>
  )
}

export default BodyOfPDFFashion

